import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Form,
  Alert,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
} from 'react-bootstrap'
import Select from 'react-select'
import axios from 'axios'
import { createRoot } from 'react-dom/client'
import toast, { Toaster } from 'react-hot-toast'
import {
  addHours,
  addWeeks,
  compareAsc,
  format,
  formatDistanceToNowStrict,
  formatISO,
  parseISO,
  roundToNearestMinutes,
  setHours,
  setMinutes,
} from 'date-fns'

import {
  TzAwareDatetimePicker,
  tzAwareNow,
} from './components/tzaware_datetime_picker'
import 'react-datepicker/dist/react-datepicker.css'

const ENDPOINT = '/api/1'

const TimelineBar = ({ reservations, startTime, endTime, roomName }) => {
  const timelineStart = new Date(startTime).setHours(0, 0, 0, 0)
  const timelineEnd = new Date(startTime).setHours(23, 59, 59, 999)
  const totalMinutes = 24 * 60

  const hasReservations =
    reservations && reservations.rooms && reservations.rooms.length > 0

  const proposedStart = new Date(startTime)
  const proposedEnd = new Date(endTime)
  const isValidProposedTime = proposedEnd > proposedStart
  const endOfDay = new Date(proposedStart).setHours(23, 59, 59, 999)
  const limitedProposedEnd = proposedEnd > endOfDay ? endOfDay : proposedEnd

  const isOverlapping =
    hasReservations &&
    reservations.rooms.some(res => {
      const resStart = new Date(res.start)
      const resEnd = new Date(res.end)
      return proposedStart < resEnd && proposedEnd > resStart
    })

  return (
    <div className="timeline-container" style={{ margin: '20px 0' }}>
      <h5>{`${roomName} - ${format(new Date(startTime), 'MMM d, yyyy')}`}</h5>
      <p>
        The grayed out times below are unavailable for booking. You may pick any
        of the remaining blank times.
      </p>
      <div className="timeline-scroll" style={{ overflowX: 'auto' }}>
        <div
          className="timeline-bar"
          style={{ height: '50px', position: 'relative', width: '1440px' }}
        >
          {/* Hour markers */}
          {[...Array(24)].map((_, index) => (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: `${(index / 24) * 100}%`,
                height: '100%',
                borderLeft: '1px solid #ccc',
                fontSize: '10px',
                paddingLeft: '2px',
              }}
            >
              {format(new Date().setHours(index, 0, 0, 0), 'ha')}
            </div>
          ))}

          {/* Existing reservations */}
          {hasReservations &&
            reservations.rooms.map((res, index) => {
              const start = new Date(res.start)
              const end = new Date(res.end)
              const startOffset =
                ((start - timelineStart) / (1000 * 60) / totalMinutes) * 100
              const duration =
                ((end - start) / (1000 * 60) / totalMinutes) * 100
              return (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    left: `${startOffset}%`,
                    width: `${duration}%`,
                    height: '100%',
                    background: '#4a4a4a',
                    opacity: 0.7,
                    zIndex: 1,
                  }}
                />
              )
            })}

          {/* Proposed reservation */}
          {isValidProposedTime && (
            <div
              style={{
                position: 'absolute',
                left: `${
                  ((proposedStart - timelineStart) /
                    (1000 * 60) /
                    totalMinutes) *
                  100
                }%`,
                width: `${
                  ((limitedProposedEnd - proposedStart) /
                    (1000 * 60) /
                    totalMinutes) *
                  100
                }%`,
                height: '100%',
                background: isOverlapping ? '#dc3545' : '#007bff',
                opacity: 0.7,
                zIndex: 2,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

const Reservation = () => {
  const [showModal, setShowModal] = useState(false)
  const [rooms, setRooms] = useState([])
  const [upcomingReservations, setUpcomingReservations] = useState([])
  const [roomDayReservations, setRoomDayReservations] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reservation, setReservation] = useState({
    resource: null,
    comment: '',
    start_datetime: tzAwareNow(),
    end_datetime: tzAwareNow(),
  })

  useEffect(() => {
    fetchRooms()
    fetchUpcomingReservations()
  }, [])

  const resetReservation = () => {
    setReservation({
      resource: null,
      comment: '',
      start_datetime: tzAwareNow(),
      end_datetime: tzAwareNow(),
    })
  }

  const handleModalClose = () => {
    resetReservation()
    setIsEditing(false)
    setIsSubmitting(false)
    setShowModal(false)
  }

  const handleInputChange = async (field, value) => {
    setReservation(prev => ({ ...prev, [field]: value }))
    if (
      field === 'resource' ||
      (field === 'start_datetime' && reservation.resource)
    ) {
      await fetchRoomDayReservations(
        field === 'resource' ? value.value : reservation.resource.value,
        field === 'start_datetime' ? value : reservation.start_datetime,
      )
    }
  }

  const fetchRooms = async () => {
    try {
      const response = await axios.get(
        `${ENDPOINT}/room_reservations/reservable_rooms`,
      )
      setRooms(response.data)
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error('Failed to fetch reservable rooms!')
      }
    }
  }

  const fetchUpcomingReservations = async () => {
    try {
      const response = await axios.get(
        `${ENDPOINT}/room_reservations/reservation_by_id`,
        {
          params: { start: tzAwareNow() },
        },
      )
      setUpcomingReservations(response.data.rooms)
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error('Failed to fetch upcoming reservations!')
      }
    }
  }

  const fetchRoomDayReservations = async (roomName, date) => {
    try {
      const response = await axios.get(
        `${ENDPOINT}/room_reservations/reservation_by_room`,
        {
          params: {
            room: roomName,
            start: new Date(date).setHours(0, 0, 0, 0),
            end: new Date(date).setHours(23, 59, 59, 999),
          },
        },
      )
      setRoomDayReservations(response.data)
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error('Failed to fetch room reservations for the selected day!')
      }
    }
  }

  const checkSameDayReservations = async reservationData => {
    try {
      const response = await axios.get(
        `${ENDPOINT}/room_reservations/reservation_by_id`,
        {
          params: {
            start: new Date(reservationData.start).setHours(0, 0, 0, 0),
            end: new Date(reservationData.end).setHours(23, 59, 59, 999),
          },
        },
      )

      const conflictingReservation = response.data.rooms.find(
        res =>
          res.room === reservationData.room &&
          (!isEditing || (isEditing && res.id !== reservationData.id)),
      )

      if (conflictingReservation) {
        toast.error('You already have a reservation for this room on this day!')
        return true
      }
      return false
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error(
          isEditing
            ? 'Failed to update reservation!'
            : 'Failed to create reservation!',
        )
      }
      return true
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const payload = {
        room: reservation.resource.value,
        start: reservation.start_datetime,
        end: reservation.end_datetime,
        comment: reservation.comment,
      }
      if (isEditing) {
        payload.id = reservation.id
      }

      const hasSameDayReservation = canReserveUnrestricted
        ? false
        : await checkSameDayReservations(payload)
      if (hasSameDayReservation) {
        return
      }

      await axios.post(`${ENDPOINT}/room_reservations/reserve`, payload)
      toast.success(
        isEditing
          ? 'Successfully updated reservation!'
          : 'Successfully reserved room!',
      )
      handleModalClose()
      await fetchUpcomingReservations()
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error(
          isEditing
            ? 'Failed to update reservation!'
            : 'Failed to create reservation!',
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async reservationId => {
    try {
      await axios.delete(`${ENDPOINT}/room_reservations/delete_reservation`, {
        params: { reservation_id: reservationId },
      })
      toast.success('Successfully deleted reservation!')
      await fetchUpcomingReservations()
    } catch (e) {
      if ('detail' in e.response.data) {
        toast.error(e.response.data.detail)
      } else {
        toast.error('Failed to delete reservation!')
      }
    }
  }

  const handleEdit = reservationToEdit => {
    setReservation({
      id: reservationToEdit.id,
      resource: {
        value: reservationToEdit.room,
        label: reservationToEdit.long_name,
      },
      comment: reservationToEdit.comment,
      start_datetime: reservationToEdit.start,
      end_datetime: reservationToEdit.end,
    })
    setIsEditing(true)
    setShowModal(true)
  }

  const validateReservation = () => {
    const start = new Date(reservation.start_datetime)
    const end = new Date(reservation.end_datetime)

    const errors = []
    if (start < new Date() || end < new Date()) {
      errors.push('Select a future date/time')
    }
    if (
      !canReserveUnrestricted &&
      (start < addHours(new Date(), 2) || start > addWeeks(new Date(), 2))
    ) {
      errors.push('Reservation must start between 2 hours and 2 weeks from now')
    }
    if (end <= start) {
      errors.push('End date/time must be after start date/time')
    }
    return errors
  }

  const selectedRoom = rooms.find(
    room => room.room === reservation.resource?.value,
  )
  const needsApproval = selectedRoom?.approval
  const roomName = selectedRoom?.room_long_name || ''

  const validationErrors = validateReservation()
  const isFormComplete =
    reservation.resource &&
    reservation.start_datetime &&
    reservation.end_datetime &&
    reservation.comment &&
    validationErrors.length === 0

  const inputGroups = [
    [
      'Room*',
      'Select a room',
      reservation.resource,
      value => setReservation({ ...reservation, resource: value }),
      'select',
      rooms.map(room => ({
        value: room.room,
        label: room.room_long_name,
      })),
    ],
    [
      'Start date/time*',
      reservation.start_datetime,
      date => setReservation({ ...reservation, start_datetime: date }),
      'date',
    ],
    [
      'End date/time*',
      reservation.end_datetime,
      date => setReservation({ ...reservation, end_datetime: date }),
      'date',
    ],
    [
      'Description*',
      'Event description',
      reservation.comment,
      comment => setReservation({ ...reservation, comment }),
      'text',
    ],
  ]

  const formatDateTime = dateTimeString => {
    return format(new Date(dateTimeString), 'MMM d yyyy, h:mm a')
  }

  return (
    <>
      <div>
        <h2>General policy</h2>
        <p>
          Individuals and groups can make reservations for available rooms
          (common kitchens, Seminar Room, Owu Room, and Music Room){' '}
          {canReserveUnrestricted ? (
            <s>
              up to <b>two weeks in advance</b> and up to{' '}
              <b>two hours prior to</b> the event
            </s>
          ) : (
            <>
              up to <b>two weeks in advance</b> and up to{' '}
              <b>two hours prior to</b> the event
            </>
          )}
          . Reservations <b>cannot be longer than three hours</b>.{' '}
          {canReserveUnrestricted ? (
            <s>
              You may only reserve a room for <b>one time block per day</b>.
            </s>
          ) : (
            <>
              You may only reserve a room for <b>one time block per day</b>.
            </>
          )}{' '}
          If your request is rejected, you should edit your original reservation
          instead of creating a new one.
        </p>
        <p>
          Study lounges and the courtyard are not reservable. Reservations for
          the Multipurpose Room can also be made here, but require the final
          approval of the{' '}
          <a href="mailto:sp-housemanager@mit.edu">House Manager</a>. To reserve
          the Multipurpose Room for external events (e.g., research labs,
          student organizations), please follow the event registration steps on
          Atlas; once they are approved, please contact the House Manager to
          schedule the space with fees applied. The House Manager is not
          responsible for approving the event and an external process needs to
          be followed.
        </p>

        <div className="d-flex gap-2 mb-4">
          <Button variant="outline-primary" onClick={() => setShowModal(true)}>
            Make a new reservation
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => window.open('/secure/calendar/', '_blank')}
          >
            View the calendar
          </Button>
        </div>

        <h2 className="mb-3">Your upcoming reservations</h2>
        {upcomingReservations.length === 0 ? (
          <p>None found.</p>
        ) : (
          <Row xs={1} md={2} lg={3} className="g-4">
            {upcomingReservations
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .map(reservation => {
                const roomNeedsApproval = rooms.find(
                  room => room.room === reservation.room,
                )?.approval
                return (
                  <Col key={reservation.id}>
                    <Card
                      className="h-100"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Card.Title as="h5" className="mb-0">
                            {reservation.long_name}
                          </Card.Title>
                          {roomNeedsApproval && (
                            <Badge
                              bg={
                                reservation.approved === 1
                                  ? 'success'
                                  : reservation.approved === 2
                                  ? 'danger'
                                  : 'warning'
                              }
                            >
                              {reservation.approved === 1
                                ? 'Approved'
                                : reservation.approved === 2
                                ? 'Rejected'
                                : 'Pending'}
                            </Badge>
                          )}
                        </div>
                        <Card.Text className="mb-1">
                          Start: {formatDateTime(reservation.start)}
                        </Card.Text>
                        <Card.Text className="mb-2">
                          End: {formatDateTime(reservation.end)}
                        </Card.Text>
                        <Card.Text className="mb-2">
                          {reservation.comment}
                        </Card.Text>
                        <div className="mt-auto d-flex justify-content-end gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(reservation)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(reservation.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )
              })}
          </Row>
        )}

        <Modal show={showModal} onHide={handleModalClose}>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Room*</Form.Label>
                <Select
                  value={reservation.resource}
                  onChange={value => handleInputChange('resource', value)}
                  options={rooms.map(room => ({
                    value: room.room,
                    label: room.room_long_name,
                  }))}
                  placeholder="Select a room"
                  isDisabled={isEditing}
                />
                {needsApproval && (
                  <div className="text-danger mt-1">
                    NOTE: Requires approval of House Manager!
                  </div>
                )}
              </Form.Group>

              {reservation.resource && (
                <TimelineBar
                  reservations={roomDayReservations}
                  startTime={reservation.start_datetime}
                  endTime={reservation.end_datetime}
                  roomName={
                    reservation.resource
                      ? reservation.resource.label
                      : 'Selected Room'
                  }
                />
              )}

              <Form.Group className="mb-3">
                <Form.Label>Start date/time*</Form.Label>
                <div className="d-flex align-items-center">
                  <TzAwareDatetimePicker
                    id="start_datetime"
                    val={reservation.start_datetime}
                    setVal={newValue =>
                      handleInputChange('start_datetime', newValue)
                    }
                    className="mb-3"
                    innerStyle={{ width: '100%' }}
                    restrictTo15={true}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>End date/time*</Form.Label>
                <div className="d-flex align-items-center">
                  <TzAwareDatetimePicker
                    id="end_datetime"
                    val={reservation.end_datetime}
                    setVal={newValue =>
                      handleInputChange('end_datetime', newValue)
                    }
                    className="mb-3"
                    innerStyle={{ width: '100%' }}
                    restrictTo15={true}
                  />
                </div>
              </Form.Group>

              {validationErrors.length > 0 && (
                <Alert variant="danger" className="p-2 mb-3">
                  <ul className="mb-0 pl-3">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Description*</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Event description"
                  value={reservation.comment}
                  onChange={e => handleInputChange('comment', e.target.value)}
                />
              </Form.Group>
            </Form>

            <div className="mt-4">
              <p>
                By making the room reservation, you agree to the following
                rules:
              </p>
              <ul>
                {roomName.toLowerCase().includes('kitchen') && (
                  <li>
                    Reservations only apply to the following items in the common
                    kitchens: tables, chairs, couches, TV, oven. Other residents
                    and staff must have access to the refrigerator, microwave
                    and trash/recycling/compost during reservations.
                  </li>
                )}
                {roomName.toLowerCase().includes('seminar') && (
                  <li>
                    The Seminar Room capacity for events is 30 people. If this
                    capacity is exceeded, the organizer may be charged a fine of
                    no more than $100.
                  </li>
                )}
                {roomName.toLowerCase().includes('owu') && (
                  <li>No food or drink is allowed in the Owu Room.</li>
                )}
                <li>
                  The room must be left clean and free from debris upon your
                  departure.
                </li>
                <li>
                  House events take priority over personal events, even if an
                  event notice has been posted.
                </li>
              </ul>
              <p>
                If alcoholic beverages are to be consumed, please note the
                following. If the event will have at least 6 attendees, the
                event organizer must apply for{' '}
                <a href="/secure/alcohol" className="inline-link">
                  small-event alcohol approval
                </a>{' '}
                and post a paper copy of any resulting approval at the entrance
                to the room. If the event will have more than 25 attendees, the
                event organizer must also{' '}
                <a href="https://atlas.mit.edu/atlas/app/events">
                  register the event with MIT
                </a>
                . Registration must be done in addition to reserving the room
                through this system.
              </p>
              <p>
                The House Manager reserves the right to apply these rules and
                guidelines on a case-by-case basis and grant exceptions.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              className="me-auto"
              onClick={handleModalClose}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!isFormComplete || isSubmitting}
            >
              {isEditing ? 'Update' : 'Submit'}
              {isSubmitting && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="ms-2"
                />
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <div>
        <Toaster />
      </div>
    </>
  )
}

export default Reservation

const container = document.getElementById('app')
const canReserveUnrestricted =
  container.dataset.hasReserveUnrestrictedPermission === 'true'
const root = createRoot(container)
root.render(<Reservation canReserveUnrestricted={canReserveUnrestricted} />)
